import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';

import layoutStyles from '@/styles/layout.module.css';
import styles from '@/styles/legal.module.css';

export function PrivacyPolicyPage(): JSX.Element {
  useSeo({
    title: buildPageTitle('Política de Privacidade'),
    description: 'Entenda quais dados o Verde Confirma trata no navegador, em serviços de infraestrutura e em integrações de terceiros.',
    canonicalPath: '/privacidade',
    ogType: 'website',
  });

  return (
    <section className={styles.page}>
      <PageBreadcrumb items={[{ label: 'Início', to: '/' }, { label: 'Política de privacidade' }]} />

      <StepHeader
        kicker="Institucional"
        title="Política de Privacidade"
        description="Explicamos quais dados podem ser tratados pelo Verde Confirma e onde eles ficam armazenados."
        actions={<Button to="/" variant="secondary" size="sm">Voltar ao início</Button>}
      />

      <div className={styles.content}>
        <Card tone="soft" className={styles.sectionCard}>
          <p className={layoutStyles.sectionTitle}>1. Dados que ficam no seu dispositivo</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>respostas, fase do fluxo, pontuação e candidatos do Match Político, armazenados localmente no navegador;</li>
            <li className={styles.listItem}>assinaturas temporárias de documentos de propostas, mantidas em cache local para evitar novas consultas;</li>
            <li className={styles.listItem}>preferências e estado de navegação que o próprio navegador decida preservar.</li>
          </ul>
        </Card>

        <Card tone="soft" className={styles.sectionCard}>
          <p className={layoutStyles.sectionTitle}>2. Dados enviados aos nossos servidores</p>
          <p className={styles.bodyText}>
            Quando você usa recursos que dependem do backend, podemos enviar o questionário, a escolha de candidato, o texto pesquisado e os parâmetros necessários para gerar respostas, metadados e documentos assinados.
          </p>
          <p className={styles.bodyText}>
            Quando você libera a visualização do Match Político, o e-mail informado pode ser enviado para registrar essa preferência de acesso e, opcionalmente, para inscrição na newsletter com o consentimento informado. Esses dados são armazenados separadamente do histórico local de respostas.
          </p>
        </Card>

        <Card tone="soft" className={styles.sectionCard}>
          <p className={layoutStyles.sectionTitle}>3. Analytics e registros técnicos</p>
          <p className={styles.bodyText}>
            Quando a configuração do projeto permitir, ferramentas de análise de uso do provedor de infraestrutura podem coletar dados agregados para medir acessos e funcionamento. O servidor também pode registrar eventos técnicos e erros para diagnóstico.
          </p>
        </Card>

        <Card tone="soft" className={styles.sectionCard}>
          <p className={layoutStyles.sectionTitle}>4. Compartilhamento e retenção</p>
          <p className={styles.bodyText}>
            Os dados são tratados apenas para viabilizar as funcionalidades do site e dos serviços de infraestrutura necessários ao projeto. Não vendemos dados pessoais.
          </p>
          <p className={styles.bodyText}>
            Você pode limpar as informações salvas localmente apagando os dados do navegador. Para inscrições em newsletter, a remoção segue os mecanismos do próprio projeto quando disponíveis.
          </p>
        </Card>

        <Card tone="soft" className={styles.sectionCard}>
          <p className={layoutStyles.sectionTitle}>5. Referência do projeto</p>
          <p className={styles.bodyText}>
            O site foi desenvolvido por{' '}
            <a className={styles.inlineLink} href="https://dikaiosun.com.br/" target="_blank" rel="noreferrer noopener">
              Dikaiosun
            </a>
            , que também pode orientar dúvidas sobre este material.
          </p>
        </Card>

        <Card className={styles.creditCard}>
          <p className={styles.creditTitle}>Controle do usuário</p>
          <p className={styles.metaText}>
            Se quiser remover respostas locais, limpe os dados do navegador. Se quiser interromper comunicações opcionais, cancele a inscrição na newsletter quando essa opção estiver disponível.
          </p>
        </Card>
      </div>
    </section>
  );
}
